import { formatInspectorValue, toLabel, isPlainObject, toNumber } from './inspector-utils.js';

/**
 * Build enhanced, user-friendly sections for the Inspector
 * This version creates a more mature UI with better organization
 */
export function buildEnhancedEventSections(entry) {
  if (!entry) {
    return [];
  }

  const { raw } = entry;
  const sections = [];
  const consumed = new Set();

  // Helper to add a section
  const addSection = (config) => {
    sections.push(config);
    if (config.consumedKeys) {
      config.consumedKeys.forEach(key => consumed.add(key));
    }
  };

  // Helper to filter out empty/null/undefined values
  const isValidValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (typeof value === 'string' && value === '—') return false;
    return true;
  };

  // ========================================================================
  // CORE EVENT INFO - Skip this section, it's already in the header
  // ========================================================================
  // Mark these keys as consumed so they don't appear elsewhere
  consumed.add('type');
  consumed.add('event');
  consumed.add('timestamp');
  consumed.add('event_type');
  consumed.add('event_name');

  // ========================================================================
  // IDENTIFIERS (Copyable IDs)
  // ========================================================================
  const idFields = [];
  if (raw.event_gid && isValidValue(raw.event_gid)) idFields.push({ label: 'Event GID', value: raw.event_gid, copyable: true });
  if (raw.message_id && isValidValue(raw.message_id)) idFields.push({ label: 'Message ID', value: raw.message_id, copyable: true });
  if (raw.entity_gid && isValidValue(raw.entity_gid)) idFields.push({ label: 'Entity GID', value: raw.entity_gid, copyable: true });
  if (raw.user_id && isValidValue(raw.user_id)) idFields.push({ label: 'User ID', value: raw.user_id, copyable: true });
  if (raw.user_gid && isValidValue(raw.user_gid)) idFields.push({ label: 'User GID', value: raw.user_gid, copyable: true });
  if (raw.anonymous_id && isValidValue(raw.anonymous_id)) idFields.push({ label: 'Anonymous ID', value: raw.anonymous_id, copyable: true });
  if (raw.previous_id && isValidValue(raw.previous_id)) idFields.push({ label: 'Previous ID', value: raw.previous_id, copyable: true });
  if (raw.session_id && isValidValue(raw.session_id)) idFields.push({ label: 'Session ID', value: raw.session_id, copyable: true });

  if (idFields.length) {
    addSection({
      type: 'definition-grid',
      title: 'Identifiers',
      icon: '🔑',
      items: idFields,
      defaultOpen: false,
      consumedKeys: ['event_gid', 'message_id', 'entity_gid', 'user_id', 'user_gid', 'anonymous_id', 'anonymous_gid', 'previous_id', 'session_id', 'session_gid', 'account_id']
    });
  }

  // ========================================================================
  // PARTICIPANTS (Enhanced display)
  // ========================================================================
  if (Array.isArray(raw.involves) && raw.involves.length) {
    addSection({
      type: 'participants',
      title: 'Participants',
      icon: '👥',
      badge: `${raw.involves.length}`,
      participants: raw.involves,
      defaultOpen: true,
      consumedKeys: ['involves']
    });
  }

  // ========================================================================
  // ENTITY LINKING
  // ========================================================================
  if (Array.isArray(raw.entity_linking) && raw.entity_linking.length) {
    const linkFields = raw.entity_linking.map((link, i) => {
      const items = [];
      if (link.label && isValidValue(link.label)) items.push({ label: 'Label', value: link.label });
      if (link.entity_type && isValidValue(link.entity_type)) items.push({ label: 'Entity Type', value: link.entity_type });
      if (link.id && isValidValue(link.id)) items.push({ label: 'ID', value: link.id, copyable: true });
      if (link.entity_gid && isValidValue(link.entity_gid)) items.push({ label: 'Entity GID', value: link.entity_gid, copyable: true });
      if (link.confidence !== undefined && link.confidence !== null) {
        const formatted = formatInspectorValue('confidence', link.confidence);
        if (isValidValue(formatted)) items.push({ label: 'Confidence', value: formatted });
      }
      return items;
    }).flat();

    if (linkFields.length) {
      addSection({
        type: 'definition-grid',
        title: 'Entity Linking',
        icon: '🔗',
        badge: `${raw.entity_linking.length}`,
        items: linkFields,
        defaultOpen: false,
        consumedKeys: ['entity_linking']
      });
    }
  }

  // ========================================================================
  // COMMERCE (with product cards)
  // ========================================================================
  const commerce = raw.commerce || null;
  let hasCommerce = false;

  // Commerce summary - Most relevant business metrics
  if (commerce) {
    const commerceFields = [];
    
    // Order ID (primary identifier)
    if (commerce.order_id && isValidValue(commerce.order_id)) {
      commerceFields.push({ label: 'Order ID', value: commerce.order_id, copyable: true });
    }
    
    // Revenue (most important metric)
    const revenue = toNumber(commerce.revenue) ?? toNumber(commerce.total);
    if (revenue !== null && !isNaN(revenue)) {
      const currency = commerce.currency || 'USD';
      commerceFields.push({ label: 'Revenue', value: `${currency} ${revenue.toFixed(2)}` });
    }
    
    // Discount (impacts margin)
    if (commerce.discount !== undefined && commerce.discount !== null && commerce.discount > 0) {
      const formatted = formatInspectorValue('discount', commerce.discount);
      if (isValidValue(formatted)) commerceFields.push({ label: 'Discount', value: formatted });
    }
    
    // Tax (compliance requirement)
    if (commerce.tax !== undefined && commerce.tax !== null && commerce.tax > 0) {
      const formatted = formatInspectorValue('tax', commerce.tax);
      if (isValidValue(formatted)) commerceFields.push({ label: 'Tax', value: formatted });
    }
    
    // Payment method
    if (commerce.payment_type && isValidValue(commerce.payment_type)) {
      const paymentStr = commerce.payment_sub_type 
        ? `${commerce.payment_type} (${commerce.payment_sub_type})`
        : commerce.payment_type;
      commerceFields.push({ label: 'Payment', value: paymentStr });
    }
    
    // Coupon code (marketing tracking)
    if (commerce.coupon && isValidValue(commerce.coupon)) {
      commerceFields.push({ label: 'Coupon', value: commerce.coupon });
    }
    
    // Store/Affiliation
    if (commerce.affiliation && isValidValue(commerce.affiliation)) {
      commerceFields.push({ label: 'Store', value: commerce.affiliation });
    }

    if (commerceFields.length) {
      addSection({
        type: 'definition-grid',
        title: 'Order Summary',
        icon: '',
        items: commerceFields,
        defaultOpen: true,
        consumedKeys: ['commerce']
      });
      hasCommerce = true;
    }
  }

  // Products (as cards, not table!)
  const products = commerce?.products || raw.products || [];
  if (Array.isArray(products) && products.length) {
    addSection({
      type: 'products',
      title: 'Order Items',
      icon: '',
      badge: `${products.length}`,
      products: products,
      defaultOpen: true,
      consumedKeys: hasCommerce ? [] : ['products']
    });
  }

  // ========================================================================
  // CLASSIFICATION (Grouped tags, not table)
  // ========================================================================
  if (Array.isArray(raw.classification) && raw.classification.length) {
    addSection({
      type: 'classification',
      title: 'Classification',
      icon: '🏷️',
      badge: `${raw.classification.length}`,
      classifications: raw.classification,
      defaultOpen: true,
      consumedKeys: ['classification']
    });
  }

  // ========================================================================
  // ANALYSIS / AI COSTS (Summary view)
  // ========================================================================
  if (Array.isArray(raw.analysis) && raw.analysis.length) {
    addSection({
      type: 'analysis',
      title: 'AI Analysis & Costs',
      icon: '🧠',
      badge: `${raw.analysis.length} calls`,
      analyses: raw.analysis,
      defaultOpen: false,
      consumedKeys: ['analysis']
    });
  }

  // ========================================================================
  // CONTENT (Special handling for chat transcripts)
  // ========================================================================
  if (raw.content && typeof raw.content === 'object') {
    const hasTranscript = raw.content.transcript || raw.content.formatted_transcript;

    if (hasTranscript) {
      // Chat conversation - special renderer
      addSection({
        type: 'chat',
        title: 'Conversation',
        icon: '💬',
        content: raw.content,
        defaultOpen: true,
        consumedKeys: []  // Keep content for other fields
      });
    }

    // Other content fields
    const contentFields = [];
    Object.entries(raw.content).forEach(([key, value]) => {
      // Skip the transcript fields as we handle them specially
      if (key === 'transcript' || key === 'formatted_transcript') return;

      // Format the value
      let formatted;
      if (typeof value === 'string' && value.length > 200) {
        formatted = value.substring(0, 200) + '...';
      } else {
        formatted = formatInspectorValue(key, value);
      }

      if (isValidValue(formatted)) {
        contentFields.push({ label: toLabel(key), value: formatted });
      }
    });

    if (contentFields.length) {
      addSection({
        type: 'definition-grid',
        title: 'Content Details',
        icon: '📝',
        items: contentFields,
        defaultOpen: false,
        consumedKeys: ['content']
      });
    } else if (hasTranscript) {
      // Mark as consumed if we only showed transcript
      consumed.add('content');
    }
  }

  // ========================================================================
  // SENTIMENT
  // ========================================================================
  if (Array.isArray(raw.sentiment) && raw.sentiment.length) {
    const sentimentItems = raw.sentiment.map(s => ({
      label: s.sentiment || s.label || 'Sentiment',
      detail: s.reason || s.target_entity,
      value: s.sentiment || s.label
    }));

    addSection({
      type: 'tags',
      title: 'Sentiment',
      icon: '😊',
      items: sentimentItems,
      defaultOpen: true,
      consumedKeys: ['sentiment']
    });
  }

  // ========================================================================
  // LOCATION (Enhanced with map links)
  // ========================================================================
  if (Array.isArray(raw.location) && raw.location.length) {
    raw.location.forEach((loc, i) => {
      if (!loc) return;

      addSection({
        type: 'location',
        title: raw.location.length > 1 ? `Location ${i + 1}` : 'Location',
        icon: '📍',
        location: loc,
        defaultOpen: true
      });
    });
    consumed.add('location');
  }

  // ========================================================================
  // PAGE CONTEXT
  // ========================================================================
  if (raw.context?.page) {
    const pageFields = [];
    const page = raw.context.page;

    if (isValidValue(page.title)) pageFields.push({ label: 'Title', value: page.title });
    if (isValidValue(page.url)) pageFields.push({ label: 'URL', value: page.url, copyable: true });
    if (isValidValue(page.path)) pageFields.push({ label: 'Path', value: page.path });
    if (isValidValue(page.referrer)) pageFields.push({ label: 'Referrer', value: page.referrer });

    if (pageFields.length) {
      addSection({
        type: 'definition-grid',
        title: 'Page',
        icon: '🌐',
        items: pageFields,
        defaultOpen: true,
        consumedKeys: [] // Keep context for device/other info
      });
    }
  }

  // For old schema where page is top-level
  if (raw.page && !raw.context?.page) {
    const pageFields = [];

    if (isValidValue(raw.page.title)) pageFields.push({ label: 'Title', value: raw.page.title });
    if (isValidValue(raw.page.url)) pageFields.push({ label: 'URL', value: raw.page.url, copyable: true });
    if (isValidValue(raw.page.path)) pageFields.push({ label: 'Path', value: raw.page.path });
    if (isValidValue(raw.page.referrer)) pageFields.push({ label: 'Referrer', value: raw.page.referrer });

    if (pageFields.length) {
      addSection({
        type: 'definition-grid',
        title: 'Page',
        icon: '🌐',
        items: pageFields,
        defaultOpen: true,
        consumedKeys: ['page']
      });
    }
  }

  // ========================================================================
  // DEVICE & BROWSER
  // ========================================================================
  const deviceFields = [];

  // From context.device
  if (raw.context?.device) {
    const d = raw.context.device;
    if (isValidValue(d.type)) deviceFields.push({ label: 'Type', value: d.type });
    if (isValidValue(d.manufacturer)) deviceFields.push({ label: 'Manufacturer', value: d.manufacturer });
    if (isValidValue(d.model)) deviceFields.push({ label: 'Model', value: d.model });
  } else if (raw.device) {
    if (isValidValue(raw.device.type)) deviceFields.push({ label: 'Type', value: raw.device.type });
    if (isValidValue(raw.device.manufacturer)) deviceFields.push({ label: 'Manufacturer', value: raw.device.manufacturer });
    if (isValidValue(raw.device.model)) deviceFields.push({ label: 'Model', value: raw.device.model });
  }

  // User agent
  const ua = raw.context?.user_agent || raw.user_agent;
  if (ua) {
    if (isValidValue(ua.platform)) deviceFields.push({ label: 'Platform', value: ua.platform });
    if (ua.mobile !== undefined) deviceFields.push({ label: 'Mobile', value: ua.mobile ? 'Yes' : 'No' });
  }

  if (deviceFields.length) {
    addSection({
      type: 'definition-grid',
      title: 'Device & Browser',
      icon: '📱',
      items: deviceFields,
      defaultOpen: false,
      consumedKeys: [] // Keep context for other info
    });
  }

  // ========================================================================
  // CAMPAIGN
  // ========================================================================
  const campaign = raw.context?.campaign || raw.campaign;
  if (campaign) {
    const campaignFields = [];
    if (isValidValue(campaign.name)) campaignFields.push({ label: 'Name', value: campaign.name });
    if (isValidValue(campaign.source)) campaignFields.push({ label: 'Source', value: campaign.source });
    if (isValidValue(campaign.medium)) campaignFields.push({ label: 'Medium', value: campaign.medium });
    if (isValidValue(campaign.term)) campaignFields.push({ label: 'Term', value: campaign.term });
    if (isValidValue(campaign.content)) campaignFields.push({ label: 'Content', value: campaign.content });

    if (campaignFields.length) {
      addSection({
        type: 'definition-grid',
        title: 'Campaign',
        icon: '🎯',
        items: campaignFields,
        defaultOpen: false,
        consumedKeys: raw.campaign ? ['campaign'] : []
      });
    }
  }

  // ========================================================================
  // METRICS & DIMENSIONS (Common in analytics events)
  // ========================================================================
  if (raw.metrics && typeof raw.metrics === 'object' && Object.keys(raw.metrics).length) {
    const metricItems = Object.entries(raw.metrics)
      .filter(([k, v]) => v !== null && v !== undefined)
      .map(([k, v]) => {
        const formatted = formatInspectorValue(k, v);
        return { label: toLabel(k), value: formatted };
      })
      .filter(item => isValidValue(item.value));

    if (metricItems.length) {
      addSection({
        type: 'definition-grid',
        title: 'Metrics',
        icon: '📊',
        items: metricItems,
        defaultOpen: false,
        consumedKeys: ['metrics']
      });
    }
  }

  if (raw.dimensions && typeof raw.dimensions === 'object' && Object.keys(raw.dimensions).length) {
    const dimensionItems = Object.entries(raw.dimensions)
      .filter(([k, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => {
        const formatted = formatInspectorValue(k, v);
        return { label: toLabel(k), value: formatted };
      })
      .filter(item => isValidValue(item.value));

    if (dimensionItems.length) {
      addSection({
        type: 'definition-grid',
        title: 'Dimensions',
        icon: '📐',
        items: dimensionItems,
        defaultOpen: false,
        consumedKeys: ['dimensions']
      });
    }
  }

  // ========================================================================
  // FLAGS (Boolean switches)
  // ========================================================================
  if (raw.flags && typeof raw.flags === 'object' && Object.keys(raw.flags).length) {
    const flagItems = Object.entries(raw.flags)
      .map(([k, v]) => ({
        label: toLabel(k),
        value: v ? '✓ Yes' : '✗ No'
      }));

    if (flagItems.length) {
      addSection({
        type: 'definition-grid',
        title: 'Flags',
        icon: '🚩',
        items: flagItems,
        defaultOpen: false,
        consumedKeys: ['flags']
      });
    }
  }

  // ========================================================================
  // PROPERTIES (Custom data)
  // ========================================================================
  if (raw.properties && typeof raw.properties === 'object' && Object.keys(raw.properties).length) {
    const propItems = Object.entries(raw.properties)
      .filter(([k]) => k !== '_normalization_changes') // Hide internal field
      .filter(([k, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => {
        let displayValue;
        if (typeof v === 'string') {
          displayValue = v.length > 100 ? v.substring(0, 100) + '...' : v;
        } else {
          displayValue = formatInspectorValue(k, v);
        }
        return { label: toLabel(k), value: displayValue };
      })
      .filter(item => isValidValue(item.value));

    if (propItems.length) {
      addSection({
        type: 'definition-grid',
        title: 'Properties',
        icon: '🔧',
        items: propItems,
        defaultOpen: false,
        consumedKeys: ['properties']
      });
    }
  }

  // ========================================================================
  // SOURCE
  // ========================================================================
  if (raw.source && typeof raw.source === 'object') {
    const sourceFields = Object.entries(raw.source)
      .map(([k, v]) => ({
        label: toLabel(k),
        value: formatInspectorValue(k, v)
      }))
      .filter(item => isValidValue(item.value));

    if (sourceFields.length) {
      addSection({
        type: 'definition-grid',
        title: 'Source',
        icon: '📡',
        items: sourceFields,
        defaultOpen: false,
        consumedKeys: ['source']
      });
    }
  }

  // ========================================================================
  // REFERRER
  // ========================================================================
  if (raw.referrer && typeof raw.referrer === 'object') {
    const referrerFields = [];
    if (isValidValue(raw.referrer.url)) referrerFields.push({ label: 'URL', value: raw.referrer.url, copyable: true });
    if (isValidValue(raw.referrer.domain)) referrerFields.push({ label: 'Domain', value: raw.referrer.domain });
    if (isValidValue(raw.referrer.type)) referrerFields.push({ label: 'Type', value: raw.referrer.type });

    Object.entries(raw.referrer).forEach(([k, v]) => {
      if (!['url', 'domain', 'type'].includes(k)) {
        const formatted = formatInspectorValue(k, v);
        if (isValidValue(formatted)) {
          referrerFields.push({ label: toLabel(k), value: formatted });
        }
      }
    });

    if (referrerFields.length) {
      addSection({
        type: 'definition-grid',
        title: 'Referrer',
        icon: '🔙',
        items: referrerFields,
        defaultOpen: false,
        consumedKeys: ['referrer']
      });
    }
  }

  // ========================================================================
  // BASE EVENTS
  // ========================================================================
  if (Array.isArray(raw.base_events) && raw.base_events.length) {
    const baseEventFields = raw.base_events.map((evt, i) => {
      const items = [];
      if (isValidValue(evt.event)) items.push({ label: `Event ${i + 1}`, value: evt.event });
      if (evt.timestamp) {
        const formatted = formatInspectorValue('timestamp', evt.timestamp);
        if (isValidValue(formatted)) items.push({ label: `Timestamp ${i + 1}`, value: formatted });
      }
      return items;
    }).flat();

    if (baseEventFields.length) {
      addSection({
        type: 'definition-grid',
        title: 'Base Events',
        icon: '🗂️',
        badge: `${raw.base_events.length}`,
        items: baseEventFields,
        defaultOpen: false,
        consumedKeys: ['base_events']
      });
    }
  }

  // ========================================================================
  // ACCESS
  // ========================================================================
  if (Array.isArray(raw.access) && raw.access.length) {
    const accessFields = raw.access.map((acc, i) => {
      const items = [];
      if (isValidValue(acc.resource)) items.push({ label: `Resource ${i + 1}`, value: acc.resource });
      if (isValidValue(acc.action)) items.push({ label: `Action ${i + 1}`, value: acc.action });
      if (acc.granted !== undefined) items.push({ label: `Granted ${i + 1}`, value: acc.granted ? 'Yes' : 'No' });
      return items;
    }).flat();

    if (accessFields.length) {
      addSection({
        type: 'definition-grid',
        title: 'Access Control',
        icon: '🔐',
        badge: `${raw.access.length}`,
        items: accessFields,
        defaultOpen: false,
        consumedKeys: ['access']
      });
    }
  }

  // ========================================================================
  // SYSTEM METADATA (Technical details)
  // ========================================================================
  const metaFields = [];

  // Event characteristics
  if (raw.importance !== undefined && raw.importance !== null) {
    metaFields.push({ label: 'Importance', value: `${raw.importance}/5` });
  }
  if (raw.customer_facing !== undefined && raw.customer_facing !== null) {
    metaFields.push({ label: 'Customer Facing', value: raw.customer_facing ? 'Yes' : 'No' });
  }

  // System fields
  if (isValidValue(raw.partition)) metaFields.push({ label: 'Partition', value: raw.partition });
  if (isValidValue(raw.write_key)) metaFields.push({ label: 'Write Key', value: raw.write_key });
  if (raw.ttl_days !== undefined && raw.ttl_days !== null) metaFields.push({ label: 'TTL Days', value: String(raw.ttl_days) });

  // Timestamps
  if (raw.received_at) {
    const formatted = formatInspectorValue('timestamp', raw.received_at);
    if (isValidValue(formatted)) metaFields.push({ label: 'Received', value: formatted });
  }
  if (raw.sent_at) {
    const formatted = formatInspectorValue('timestamp', raw.sent_at);
    if (isValidValue(formatted)) metaFields.push({ label: 'Sent', value: formatted });
  }
  if (raw.local_time) {
    const formatted = formatInspectorValue('timestamp', raw.local_time);
    if (isValidValue(formatted)) metaFields.push({ label: 'Local Time', value: formatted });
  }
  if (raw.original_timestamp) {
    const formatted = formatInspectorValue('timestamp', raw.original_timestamp);
    if (isValidValue(formatted)) metaFields.push({ label: 'Original', value: formatted });
  }

  // Analysis and integration flags
  if (raw.analyse && typeof raw.analyse === 'object' && Object.keys(raw.analyse).length) {
    const flagsList = Object.entries(raw.analyse).filter(([,v]) => v).map(([k]) => k).join(', ');
    if (isValidValue(flagsList)) metaFields.push({ label: 'Analysis Flags', value: flagsList });
  }
  if (raw.integrations && typeof raw.integrations === 'object' && Object.keys(raw.integrations).length) {
    const integrationsList = Object.entries(raw.integrations).filter(([,v]) => v).map(([k]) => k).join(', ');
    if (isValidValue(integrationsList)) metaFields.push({ label: 'Integrations', value: integrationsList });
  }

  if (metaFields.length) {
    addSection({
      type: 'definition-grid',
      title: 'System Metadata',
      icon: '⚙️',
      items: metaFields,
      defaultOpen: false,
      consumedKeys: ['importance', 'customer_facing', 'partition', 'write_key', 'ttl_days', 'received_at', 'sent_at', 'local_time', 'original_timestamp', 'analyse', 'integrations']
    });
  }

  // ========================================================================
  // ALTERATIONS - Moved to separate "Alterations" tab
  // ========================================================================
  // Normalization changes are now displayed in their own tab next to Raw JSON
  // The DeveloperView component handles parsing and display

  // ========================================================================
  // OTHER UNCONSUMED FIELDS (Fallback)
  // ========================================================================
  const unconsumedFields = Object.entries(raw)
    .filter(([key]) => !consumed.has(key))
    .filter(([key]) => key !== 'context') // Handle context specially
    .filter(([, value]) => value !== null && value !== undefined)
    .filter(([, value]) => !isPlainObject(value) && !Array.isArray(value))
    .map(([key, value]) => ({
      label: toLabel(key),
      value: formatInspectorValue(key, value)
    }))
    .filter(item => isValidValue(item.value));

  if (unconsumedFields.length) {
    addSection({
      type: 'definition-grid',
      title: 'Additional Fields',
      icon: '📄',
      items: unconsumedFields,
      defaultOpen: false
    });
  }

  return sections;
}
